import React from 'react';
import PropTypes from 'prop-types';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Input from '@components/form/Input';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import { ButtonSticky } from '@components/screen/Layout';
import Colors from '@global/colors';
import { isAndroid } from '@global/screens';
import VisibilityResultProductList from './VisibilityResultProductList';
import VisibilityResultOtherProductList from './VisibilityResultOtherProductList';

const VisibilitySelectedSection = ({
  lastPayload = [],
  lastPayloadOther = [],
  searchQuery = '',
  setSearchQuery = () => {},
  activeTab = 'default',
  setActiveTab = () => {},
  filterProducts = (val) => val,
  detailData = {},
  detailTodo = {},
  handleProductUpdate = () => {},
  customerAddress = {},
  navigation = {},
  showProduct = false,
  setShowProduct = () => {},
  missingFieldData = [],
  isLoadingGlobal = false,
  onSubmitProduct = () => {},
  setMissingFieldData = () => {},
  setModalValidation = () => {},
  modalValidation = false,
  accountGroupId = null,
  isValueSellOutOnly = false,
}) => {
  return (
    <View testID="todo-visibility-selected-section" style={styles.wrapper}>
      {lastPayload?.length || lastPayloadOther?.length ? (
        <View style={styles.searchWrapper}>
          <Input
            placeholder="Search product by name, reference, or brand"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
      ) : null}

      <View style={styles.tabHeader}>
        <TouchableOpacity
          style={[
            styles.tabButton,
            activeTab === 'default' && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab('default')}>
          <SociollaText
            style={[
              styles.tabLabel,
              styles.tabText,
              activeTab === 'default' && styles.tabTextActive,
            ]}>
            {'Products ('}
            {lastPayload?.length || 0}
            {')'}
          </SociollaText>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.tabButton,
            activeTab === 'other' && styles.tabButtonActive,
          ]}
          onPress={() => setActiveTab('other')}>
          <SociollaText
            style={[
              styles.tabLabel,
              styles.tabText,
              activeTab === 'other' && styles.tabTextActive,
            ]}>
            {'Other Products ('}
            {lastPayloadOther?.length || 0}
            {')'}
          </SociollaText>
        </TouchableOpacity>
      </View>

      {activeTab === 'default' ? (
        <VisibilityResultProductList
          products={lastPayload}
          filterProducts={filterProducts}
          detailData={detailData}
          detailTodo={detailTodo}
          handleProductUpdate={handleProductUpdate}
          customerAddress={customerAddress}
          navigation={navigation}
          showProduct={showProduct}
          accountGroupId={accountGroupId}
          isValueSellOutOnly={isValueSellOutOnly}
        />
      ) : (
        <VisibilityResultOtherProductList
          products={lastPayloadOther}
          hasDefaultProducts={Boolean(lastPayload?.length)}
          filterProducts={filterProducts}
          detailData={detailData}
          detailTodo={detailTodo}
          handleProductUpdate={handleProductUpdate}
          customerAddress={customerAddress}
          navigation={navigation}
          showProduct={showProduct}
          accountGroupId={accountGroupId}
          isValueSellOutOnly={isValueSellOutOnly}
        />
      )}

      {missingFieldData.length === 0 ? (
        <ButtonSticky
          show
          customHost="modal-sticky-button"
          buttons={[
            {
              component: (
                <Button
                  rounded
                  type="default"
                  buttonStyle={styles.addButton}
                  title={
                    <SociollaText type="bold" style={styles.addButtonText}>
                      Add Product
                    </SociollaText>
                  }
                  onPress={() => {
                    setShowProduct(true);
                  }}
                />
              ),
            },
            {
              title: 'Submit',
              disabled:
                (lastPayload?.length || lastPayloadOther?.length) &&
                !isLoadingGlobal
                  ? false
                  : true,
              onPress: () => onSubmitProduct(),
            },
          ]}
        />
      ) : (
        modalValidation && (
          <ButtonSticky
            show
            customHost="modal-sticky-button"
            buttons={[
              {
                title: 'Close',
                onPress: () => {
                  setMissingFieldData([]);
                  setModalValidation(false);
                },
              },
            ]}
          />
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    // Reserve for the sticky bar, which overlaps the bottom of this section.
    // ModalFull already adds marginBottom: 56 to its content, so this only needs
    // to cover the rest. 160 left a visible gap on Android once edge-to-edge made
    // the bar taller; 60 was measured on device (B2B-5805). iOS and web keep 160
    // as ButtonSticky does not add the bottom inset there.
    paddingBottom: isAndroid ? 60 : 160,
  },
  searchWrapper: {
    padding: 10,
  },
  tabHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    backgroundColor: Colors.white,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabButtonActive: {
    borderBottomWidth: 2,
    borderBottomColor: Colors.blue0,
  },
  tabText: {
    fontSize: 14,
    color: Colors.grey2,
  },
  tabLabel: {
    letterSpacing: 0.5,
  },
  tabTextActive: {
    color: Colors.blue0,
  },
  addButton: {
    width: '100%',
  },
  addButtonText: {
    fontSize: 14,
    letterSpacing: 0.5,
  },
});

VisibilitySelectedSection.propTypes = {
  lastPayload: PropTypes.array,
  lastPayloadOther: PropTypes.array,
  searchQuery: PropTypes.string,
  setSearchQuery: PropTypes.func,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
  filterProducts: PropTypes.func,
  detailData: PropTypes.object,
  detailTodo: PropTypes.object,
  handleProductUpdate: PropTypes.func,
  customerAddress: PropTypes.object,
  navigation: PropTypes.object,
  showProduct: PropTypes.bool,
  setShowProduct: PropTypes.func,
  missingFieldData: PropTypes.array,
  isLoadingGlobal: PropTypes.bool,
  onSubmitProduct: PropTypes.func,
  setMissingFieldData: PropTypes.func,
  setModalValidation: PropTypes.func,
  modalValidation: PropTypes.bool,
  accountGroupId: PropTypes.any,
  isValueSellOutOnly: PropTypes.bool,
};

export default React.memo(VisibilitySelectedSection);
